import { useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { num, timeAgo, relDate } from "../lib/format";
import { Icon } from "../components/icons";
import { Empty } from "../components/ui";
import type { Integration, Connector, SyncRun } from "../lib/types";
import * as work from "../lib/workClient";

const DATA_FILTERS = ["All", "Structured", "Unstructured"] as const;

function dataBadge(dt: Integration["dataType"]) {
  if (dt === "structured") return <span className="badge badge--up">structured</span>;
  if (dt === "unstructured") return <span className="badge badge--watch">unstructured</span>;
  return <span className="badge badge--good">structured + unstructured</span>;
}

function directionLabel(d: Integration["direction"]) {
  return d === "inbound" ? "Source →" : d === "outbound" ? "→ Destination" : "↔ Bi-directional";
}

export default function Integrations() {
  const { ws } = useWorkspace();
  const [dataFilter, setDataFilter] = useState<(typeof DATA_FILTERS)[number]>("All");
  const [onlyConnected, setOnlyConnected] = useState(false);

  const list = useMemo(() => {
    return ws.integrations.filter((i) => {
      if (onlyConnected && i.status !== "connected") return false;
      if (dataFilter === "Structured") return i.dataType === "structured" || i.dataType === "both";
      if (dataFilter === "Unstructured") return i.dataType === "unstructured" || i.dataType === "both";
      return true;
    });
  }, [ws.integrations, dataFilter, onlyConnected]);

  const connected = ws.integrations.filter((i) => i.status === "connected");
  const recordsSynced = connected.reduce((s, i) => s + (i.recordsSynced ?? 0), 0);
  const unstructuredCt = ws.integrations.filter((i) => i.dataType !== "structured").length;
  const lastSync = connected
    .map((i) => i.lastSync)
    .filter(Boolean)
    .sort()
    .slice(-1)[0];

  const byCategory = useMemo(() => {
    const groups: Record<string, Integration[]> = {};
    for (const i of list) (groups[i.category] = groups[i.category] || []).push(i);
    return Object.entries(groups);
  }, [list]);

  return (
    <div className="page">
      <div className="mb-24">
        <p className="eyebrow">Integrations</p>
        <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Every source, structured or not</h1>
        <p className="page__intro">
          Penumbrai reads from the systems your customers actually live in — the warehouse, the CRM,
          the product, and the conversations. Structured tables and unstructured text land in the same
          model, so a support ticket can move a health score just like a usage metric.
        </p>
      </div>

      <div className="stats">
        <div className="stat">
          <div className="stat__label"><Icon name="plug" size={14} /> Connected sources</div>
          <div className="stat__value">{connected.length}<small> / {ws.integrations.length}</small></div>
          <div className="stat__foot">across {byCategory.length || new Set(ws.integrations.map((i) => i.category)).size} categories</div>
        </div>
        <div className="stat">
          <div className="stat__label"><Icon name="database" size={14} /> Records synced</div>
          <div className="stat__value">{num(recordsSynced)}</div>
          <div className="stat__foot">last 24h</div>
        </div>
        <div className="stat">
          <div className="stat__label"><Icon name="sparkle" size={14} /> Unstructured sources</div>
          <div className="stat__value">{unstructuredCt}</div>
          <div className="stat__foot">tickets, calls, email, surveys</div>
        </div>
        <div className="stat">
          <div className="stat__label"><Icon name="refresh" size={14} /> Last sync</div>
          <div className="stat__value" style={{ fontSize: 26 }}>{lastSync ? `${timeAgo(lastSync)} ago` : "—"}</div>
          <div className="stat__foot">continuous</div>
        </div>
      </div>

      <div className="toolbar">
        <div className="seg-control">
          {DATA_FILTERS.map((f) => (
            <button key={f} className={dataFilter === f ? "on" : ""} onClick={() => setDataFilter(f)}>{f}</button>
          ))}
        </div>
        <button className={`chip ${onlyConnected ? "chip--active" : ""}`} onClick={() => setOnlyConnected((v) => !v)}>
          <Icon name="check" size={13} /> Connected only
        </button>
        <div className="grow" />
        <button className="btn btn--primary btn--sm"><Icon name="plus" size={14} /> Add integration</button>
      </div>

      <ConnectorsCard />

      {byCategory.map(([category, items]) => (
        <div key={category} style={{ marginBottom: 26 }}>
          <div className="section-title" style={{ fontSize: 15 }}>{category}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
            {items.map((i) => (
              <div className="card" key={i.id}>
                <div className="card__bd">
                  <div className="row between" style={{ alignItems: "flex-start" }}>
                    <div className="row" style={{ gap: 12 }}>
                      <span className="avatar avatar--lg" style={{ background: i.color, fontFamily: "var(--serif)", fontSize: 18 }}>{i.name[0]}</span>
                      <div>
                        <b style={{ fontSize: 15, fontWeight: 600 }}>{i.name}</b>
                        <div className="faint ff-mono" style={{ fontSize: 11 }}>{directionLabel(i.direction)}</div>
                      </div>
                    </div>
                    {i.status === "connected" ? (
                      <span className="badge badge--good">connected</span>
                    ) : (
                      <button className="btn btn--ghost btn--sm">Connect</button>
                    )}
                  </div>

                  <p className="muted" style={{ fontSize: 13, margin: "12px 0 14px" }}>{i.blurb}</p>

                  <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
                    {dataBadge(i.dataType)}
                    {i.syncs.length > 0 && <span className="tag">→ {i.syncs.join(", ")}</span>}
                  </div>

                  {i.status === "connected" && (
                    <div className="row between" style={{ borderTop: "1px solid var(--line-soft)", paddingTop: 10 }}>
                      <span className="faint" style={{ fontSize: 12 }}>
                        {i.recordsSynced ? `${num(i.recordsSynced)} records` : "active"}
                      </span>
                      <span className="faint ff-mono" style={{ fontSize: 11 }}>
                        {i.lastSync ? `synced ${timeAgo(i.lastSync)} ago` : ""}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="card" style={{ background: "var(--corona-soft)", borderColor: "rgba(201,130,26,.3)" }}>
        <div className="card__bd row" style={{ gap: 14, alignItems: "flex-start" }}>
          <span className="mono" style={{ width: 40, height: 40, borderRadius: 11, background: "var(--surface)", color: "var(--corona-ink)" }}>
            <Icon name="sparkle" size={18} />
          </span>
          <div>
            <b style={{ fontSize: 14.5, fontWeight: 600 }}>Text becomes signal</b>
            <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
              Unstructured sources aren't just stored — every ticket, transcript, and email is enriched with
              sentiment, themes, and a summary, mapped to the right account, and made available to health
              scores, segments, and playbooks. See the extracted fields on the{" "}
              <span className="ff-mono" style={{ color: "var(--corona-ink)" }}>conversation</span> object in Data Studio.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_CLASS: Record<SyncRun["status"], string> = { success: "badge--good", partial: "badge--watch", failed: "badge--risk" };

function ConnectorsCard() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [runs, setRuns] = useState<SyncRun[]>(ws.syncRuns);
  const [busy, setBusy] = useState<string>("");

  useEffect(() => { work.loadConnectors(ws).then((r) => { setMode(r.mode); setConnectors(r.connectors); setRuns(r.runs); }); }, [ws]);

  const sync = async (c: Connector) => {
    setBusy(c.key);
    const run = await work.runConnector(mode, ws, c.key);
    setRuns((prev) => [run, ...prev]);
    setBusy("");
  };

  return (
    <div className="card" style={{ marginBottom: 26 }}>
      <div className="card__hd">
        <div><h3><Icon name="refresh" size={15} /> Live connectors</h3><span className="sub">Pull external data into the flexible model on demand</span></div>
        <span className={`mode-pill ${mode === "api" ? "live" : ""}`}><i />{mode === "api" ? "Live API" : "Local demo"}</span>
      </div>
      <div className="card__bd" style={{ display: "grid", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {connectors.map((c) => (
            <div key={c.key} className="hook-row">
              <div className="row between" style={{ alignItems: "center" }}>
                <div>
                  <b style={{ fontSize: 13.5 }}>{c.label}</b>
                  <div className="faint" style={{ fontSize: 12 }}>→ {c.object} · {c.kind === "sample" ? "sample data" : "HTTP JSON"}</div>
                </div>
                <button className="btn btn--primary btn--sm" disabled={busy === c.key || c.kind === "http_json"} title={c.kind === "http_json" ? "Configure a URL via the API to sync" : ""} onClick={() => sync(c)}>
                  {busy === c.key ? "Syncing…" : "Run sync"}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="labeled__l" style={{ marginBottom: 8 }}>Sync history</div>
          {runs.length === 0 ? <Empty>No syncs yet — run a connector above.</Empty> : (
            <table className="table">
              <thead><tr><th>Connector</th><th>Into</th><th>Created</th><th>Status</th><th>When</th></tr></thead>
              <tbody>
                {runs.slice(0, 8).map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontSize: 12.5 }}>{r.connectorLabel}</td>
                    <td className="faint" style={{ fontSize: 12, fontFamily: "var(--mono)" }}>{r.object}</td>
                    <td className="num">{r.created}{r.failed ? <span style={{ color: "var(--risk)" }}> · {r.failed} failed</span> : ""}</td>
                    <td><span className={`badge ${STATUS_CLASS[r.status]}`}>{r.status}</span></td>
                    <td className="faint" style={{ fontSize: 12 }}>{relDate(r.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
